import { itemsCol, layerOf, layerHas, layerEntriesOf, layerListEntryUuid, scalarLayerSlot,
    plTotal, plPretty, plExact, sourceOf } from '../shared/array-types';
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
import { statusRuneMountHtml } from '../shared/status-rune';
import { makeNumberPretty } from '../object-card/helpers';
import type { CellSegment, TableCell, TableColumn, TableRow } from './types';
import type { FromBaseRow } from '../janus-engine/compile/from-base-context';

type Family = 'spells' | 'abilities';
const icon = (id: string): CellSegment => ({ kind: 'icon', id });
const text = (value: unknown): CellSegment => ({ kind: 'text', value: String(value ?? '') });
const cell = (sort: string | number = '', segments: CellSegment[] = [], label = String(sort)): TableCell =>
    ({ segments, sort, text: label.toLowerCase() });
const dash = (): TableCell => cell('', [text('—')], '');
const refs = (row: FromBaseRow, key: string): string[] => layerEntriesOf(row, key).map(layerListEntryUuid).filter(Boolean);
const value = (row: FromBaseRow, key: string): string => scalarLayerSlot(layerOf(row, key));
const name = (row: unknown): string => decodeHtmlEntitiesForPlainText(itemsCol(row, 'item_name') ?? '');
const link = (id: string): string => `/item.php?id=${encodeURIComponent(id)}`;
const numeric = (v: string): string | number => v !== '' && Number.isFinite(Number(v)) ? Number(v) : '';

/** One browser column specification for both raw definition-list families. */
export function definitionColumns(family: Family, itemType: string): TableColumn[] {
    const column = (key: string, label: string, id = key, sortType: 'num' | 'text' = 'text',
        align: 'left' | 'center' | 'right' = 'center', width?: string): TableColumn => ({ key,
        header_label: label, header_segments: [icon(id)], sortable: true, filterable: key === 'name', sort_type: sortType, align, width });
    const pl = column('pl', 'Power Level', 'power_level', 'num', 'right');
    if (family === 'abilities') return [
        column('name', itemType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), itemType, 'text', 'left'),
        column('requires_class', 'Requires Class', 'requires_class', 'text', 'center', '75px'),
        column('roll_type', 'Roll Type', 'roll_type', 'text', 'center', '50px'),
        column('dice', 'Dice', 'dice', 'text', 'left'),
        column('charges', 'Charges', 'ability_activation_charges', 'num', 'right', '75px'),
        column('unenchantable', 'Unenchantable', 'unenchantable', 'text', 'center', '50px'), pl,
    ];
    const columns = [column('name', 'Name', 'spell', 'text', 'left', '22%'),
        column('level', 'Level', 'spell_level', 'num'), column('sphere', 'Sphere', 'sphere_of_magic'),
        column('circle', 'Circle', 'circle_of_magic'), column('class', 'Class | Subclass'),
        column('components', 'Components'), column('casting_time', 'Casting Time', 'spell_casting_time'),
        column('range', 'Range'), column('aoe', 'AOE'), column('max_targets', 'Max Targets', 'max_target', 'num', 'center', '60px'),
        column('roll_type', 'Roll Type'), column('duration', 'Duration', 'duration', 'text', 'right'),
        column('upcastable', 'Upcastable', 'spell_upcastable', 'text', 'right'), pl];
    for (const col of columns) {
        if (['components', 'aoe'].includes(col.key)) col.header_segments = [text(col.header_label)];
        if (col.key === 'class') {
            col.sortable = false;
            col.header_segments = [icon('requires_class'), text(' | '), icon('requires_subclass')];
        }
    }
    return columns;
}

export function definitionNameCell(row: FromBaseRow): TableCell {
    const id = String(itemsCol(row, 'item_id') ?? '');
    const label = name(row);
    return cell(label, [{ kind: 'raw', html: statusRuneMountHtml(row, { size: 72, className: 'ft-rune-catalog' }) },
        { kind: 'text', value: label, link: link(id) }]);
}

function classCell(row: FromBaseRow, catalog: Record<string, unknown>): TableCell {
    const layer = layerHas(row, 'requires_subclass') ? 'requires_subclass' : 'requires_class';
    const level = value(row, 'requires_class_level'), labels: string[] = [];
    const segments = layerEntriesOf(row, layer).flatMap((entry): CellSegment[] => {
        const id = layerListEntryUuid(entry);
        if (!id) return [];
        const source = catalog[id] ?? catalog[id.toUpperCase()];
        const label = name(entry) || name(source) || id;
        labels.push(label);
        return [{ kind: 'icon', id, w: 40, h: 40, item_type: layer === 'requires_subclass' ? 'subclass' : 'class',
            link: link(id), target: '_blank', title: label, ...(level !== '' ? { badge: level } : {}) }];
    });
    return cell(labels.join(' ').trim(), segments);
}

export function definitionPlCell(pl: number | undefined, id: string): TableCell {
    if (pl !== undefined) return cell(pl, [{ kind: 'text', value: plPretty(pl), tip: 'Power Level: ' + plExact(pl) }], '');
    return { ...cell('pending', [{ kind: 'text', value: '—', cls: 'text-muted ft-pl-pending', tip: 'Power Level: pending' }]),
        pl: { stored: 0, pending_ids: [id.toUpperCase()], pending_other: 0 } };
}

/** Damage/upcast are the same expression with different authored slots. */
function damageCell(row: FromBaseRow, upcast = false, ability = false): TableCell {
    const suffix = upcast ? '_upcast' : '', die = value(row, 'die' + suffix), dice = value(row, 'dice' + suffix);
    const mod = value(row, 'mod' + suffix), segments: CellSegment[] = [], labels: string[] = [];
    if (die !== '') { segments.push(text(dice + 'd' + die)); labels.push(dice + 'd' + die); }
    if (ability && value(row, 'versatile_die') !== '') {
        const versatile = '(' + value(row, 'versatile_dice') + 'd' + value(row, 'versatile_die') + ')';
        segments.push(text(' ' + versatile)); labels.push(versatile);
    }
    if (mod !== '' && mod !== '0') {
        if (!upcast && !ability) segments.push({ kind: 'br' });
        segments.push(text(ability || upcast ? ' + ' : '+ '));
        segments.push(mod === '[spellcasting_essence]' && !ability ? icon('spellcasting_essence') : text(mod));
        labels.push(mod === '[spellcasting_essence]' && !ability ? 'spellcasting_essence' : (ability ? '+ ' : '') + mod);
    }
    const type = upcast ? '' : value(row, 'damage_type');
    if (type !== '') { segments.push(text(' '), icon(type)); if (!ability) labels.push(type); }
    const formula = labels.join(' ').trim();
    return cell(formula, segments);
}

/** The optional rune view never changes the saved root read by the other cells. */
export function definitionRow(row: FromBaseRow, family: Family, catalog: Record<string, unknown>, runeView: FromBaseRow = row): TableRow {
    const v = (key: string) => value(row, key), has = (key: string) => layerHas(row, key);
    const numberCell = (key: string) => cell(numeric(v(key)), v(key) === '' ? [] : [text(v(key))], v(key));
    const refCell = (key: string) => cell(v(key), v(key) === '' ? [] : [icon(v(key))]);
    const pl = plTotal(row), cells = { name: definitionNameCell(runeView), pl: definitionPlCell(pl, String(itemsCol(row, 'item_id') ?? '')) } as Record<string, TableCell>;
    const filters: Record<string, string | string[]> = {
        requires_class: refs(row, 'requires_class'), requires_subclass: refs(row, 'requires_subclass'),
        sources: String(itemsCol(sourceOf(row), 'source_id') ?? ''), pl: pl === undefined ? '' : String(pl),
    };
    const req = classCell(row, catalog);
    if (family === 'abilities') {
        const charges = v('ability_activation_charges'), unench = v('unenchantable') !== '' && v('unenchantable') !== '0';
        Object.assign(cells, { requires_class: req, roll_type: refCell('roll_type'), dice: damageCell(row, false, true),
            charges: cell(charges !== '' && charges !== '0' ? numeric(charges) : '',
                charges !== '' && charges !== '0' ? [text(makeNumberPretty(charges))] : [], ''),
            unenchantable: cell(unench ? 1 : 0, unench ? [icon('unenchantable')] : [], '') });
        Object.assign(filters, { roll_type: v('roll_type') ? [v('roll_type')] : [], damage_type: v('damage_type') ? [v('damage_type')] : [],
            dice: v('dice') === '' ? '' : String(numeric(v('dice'))), die: v('die') === '' ? '' : String(numeric(v('die'))),
            charges: charges === '0' ? '' : charges, unenchantable: unench ? ['1'] : [] });
    } else {
        const sphereIds = refs(row, 'spell_sphere'), components = ['spell_component_verbal', 'spell_component_somatic', 'spell_component_material_consumed'].filter(has);
        const castingLabels: Record<string, string> = { one_minute: '1 Min', ten_minutes: '10 Min', one_hour: '1 Hr', eight_hours: '8 Hrs', twelve_hours: '12 Hrs', twenty_four_hours: '24 Hrs' };
        const ct = v('spell_casting_time'), ctSegments: CellSegment[] = has('spell_ritual') ? [icon('spell_ritual'), { kind: 'br' }] : [];
        if (['3ap', '2ap', '1ap', 'reaction'].includes(ct)) ctSegments.push(icon(ct));
        if (castingLabels[ct]) ctSegments.push(text(castingLabels[ct]));
        const aoe = v('aoe_type'), distance = v('aoe_distance'), max = v('max_targets');
        const duration = v('duration_int'), durationType = v('duration_type');
        const durationLabels: Record<string, string> = { hours: 'hrs', minutes: Number(duration) === 1 ? 'min' : 'mins', days: Number(duration) === 1 ? 'day' : 'days', months: Number(duration) === 1 ? 'month' : 'months', years: Number(duration) === 1 ? 'year' : 'years' };
        const durationSegments: CellSegment[] = has('spell_concentration') ? [icon('concentration')] : [];
        const durationText: string[] = [];
        if (duration === '0' && durationType === '') { durationSegments.push(text('Instant')); durationText.push('instant'); }
        if (duration === '' && durationType === 'permanent') { durationSegments.push(icon('duration_permanent')); durationText.push('permanent'); }
        if (duration !== '' && durationType !== '') {
            durationSegments.push(text(duration + ' ' + (durationLabels[durationType] ?? '')));
            durationText.push(duration + ' ' + (durationLabels[durationType] ?? durationType));
        }
        const roll = v('roll_type') === 'AD80D31D-898B-66CE-0894-1335D11B1529' ? v('skill_roll_contestee') : v('roll_type');
        const rollSegments: CellSegment[] = roll ? [icon(roll)] : [text('—')];
        if (has('roll_type') && has('defensive_roll_successful_for_half_damage')) rollSegments.push({ kind: 'badge', value: '½', variant: 'warning text-dark' });
        const damage = damageCell(row);
        if (roll && damage.text) rollSegments.push(text(' '), ...(damage.segments ?? []));
        const upcastable = has('spell_upcastable') || has('spell_scalable_cantrip');
        const upcast = upcastable ? damageCell(row, true) : dash();
        if (upcastable && !has('mod_upcast') && !has('die_upcast')) Object.assign(upcast, cell('y', [text('Y')]));
        Object.assign(cells, { level: numberCell('spell_level'), sphere: cell(sphereIds.join(' '), sphereIds.map(id => ({ kind: 'icon', id, item_type: 'sphere_of_magic' }))),
            circle: refCell('spell_circle'), class: req, components: cell(components.join(' '), components.map(icon)),
            casting_time: cell(ct, ctSegments, (ct + ' ' + (castingLabels[ct] ?? '')).trim()), range: refCell('range'),
            aoe: aoe ? cell(aoe, [icon(aoe), { kind: 'br' }, ...(distance === '' ? [] : [text(distance)])], (aoe + ' ' + distance).trim()) : dash(),
            max_targets: cell(numeric(max), max === '' ? [text('—')] : Number(max) >= 50 ? [icon('max_target')] : [text(max)], max),
            duration: cell(numeric(duration), durationSegments, durationText.join(' ')),
            roll_type: cell((roll + ' ' + damage.sort).trim(), rollSegments, (roll + ' ' + damage.text).trim()), upcastable: upcast });
        for (const key of ['spell_level', 'spell_circle', 'spell_casting_time', 'roll_type', 'damage_type']) filters[key] = v(key);
        for (const key of ['spell_ritual', 'spell_concentration', 'spell_component_verbal', 'spell_component_somatic', 'spell_component_material', 'spell_component_material_consumed']) filters[key] = has(key) ? '1' : '';
        Object.assign(filters, { spell_upcastable: upcastable ? '1' : '', spell_sphere: sphereIds });
    }
    return { item_id: String(itemsCol(row, 'item_id') ?? ''), cells, filters };
}
